import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import API from "../../config/axios";
import { api_paths } from "../../config/api.paths";
import DatePicker from "../common/DatePicker";
import SelectField from "../common/SelectField";
import { validateData, clearError } from "../../validators/YupValidations";
import { memberSchema } from "../../validators/validatorSchema";
import { toastErrorMessage, toastSuccessMessage } from "../../utils/utils";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { SET_LOADING } from "../../redux/user";
import { formatUSPhone, normalizePhoneForSave } from "../../utils/phoneFormat";
import { normalizeDateToUTC } from "../../utils/dateFormat";
import { useMemo, useRef } from "react";
import { OverlayTrigger, Tooltip, Image } from "react-bootstrap";
import ProfilePhotoCropper from "./ProfilePhotoCropper";
import ConfirmDeleteModal from "../common/ConfirmDeleteModal";
import editIcon from "../../assets/images/icons/edit.svg";
import deleteIcon from "../../assets/images/icons/close.svg";

const Field = ({ label, required, children, error }) => (
  <div className="mb-3 pb-1">
    <label className="form-label small mb-2">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    {children}
    {error && <div className="text-danger small mt-1">{error}</div>}
  </div>
);

const MemberModal = ({ show, onHide, editData, refreshList, onSaveLocal, existingItems = [] }) => {
  const [form, setForm] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    date_of_birth: null,
    gender: "",
    relationship_id: "",
    marital_status: null,
    phone: "",
    email: "",
    country_code: editData?.country_code || "+1",
  });

  const [errors, setErrors] = useState({});
  const [relationships, setRelationships] = useState([]);
  const { loading } = useSelector((state) => state.USER_DATA);
  const dispatch = useDispatch();

  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [showPhotoDeleteModal, setShowPhotoDeleteModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [tempImagePreview, setTempImagePreview] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const fileInputRef = useRef(null);
  const IMAGE_BASEURL = process.env.REACT_APP_IMAGE_BASEURL || "";

  const profileImage = useMemo(() => {
    if (photoRemoved) return null;
    if (tempImagePreview) return tempImagePreview;
    const raw =
      editData?.profile_picture ||
      editData?.profile_picture_path ||
      editData?.image ||
      "";
    if (!raw) return null;
    if (raw.startsWith("http") || raw.startsWith("data:")) return raw;
    return `${IMAGE_BASEURL}${raw}`;
  }, [editData, IMAGE_BASEURL, tempImagePreview, photoRemoved]);

  const initials = useMemo(() => {
    const first = form?.first_name?.[0] || "";
    const last = form?.last_name?.[0] || "";
    return `${first}${last}`.toUpperCase() || "?";
  }, [form]);

  const uploadPhotoForMember = async (memberId, file) => {
    try {
      const formData = new FormData();
      formData.append("profile_picture", file);
      await API.put(
        `${api_paths.member.family_member.EDIT_PROFILE}${memberId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
    } catch (error) {
      console.error("Failed to upload photo:", error);
      toastErrorMessage("Failed to upload profile photo.");
    }
  };

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  const maritalStatusOptions = [
    { value: "single", label: "Single" },
    { value: "married", label: "Married" },
    { value: "divorced", label: "Divorced" },
    { value: "widowed", label: "Widowed" },
  ];

  const countryCodeOptions = [
    { value: "+1", label: "+1" },
    { value: "+91", label: "+91" },
  ];

  const loadRelationships = async () => {
    try {
      dispatch(SET_LOADING(true));
      const res = await API.get(
        api_paths.member.family_member.GET_RELATIONSHIP
      );
      setRelationships(res.data?.payload?.result || []);
    } catch (err) {
      console.log("Failed to load relationships", err);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };
  useEffect(() => {
    loadRelationships();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (show) {
      // Reset form when modal is closed
      if (editData) {
        setForm({
          first_name: editData.first_name || "",
          middle_name: editData.middle_name || "",
          last_name: editData.last_name || "",
          date_of_birth: editData.date_of_birth
            ? new Date(editData.date_of_birth)
            : null,
          gender: editData.gender || "",
          relationship_id: editData.relationship_id || "",
          marital_status: editData.marital_status || null,
          phone:
            editData.country_code === "+1"
              ? formatUSPhone(editData.phone || "")
              : editData.phone || "",
          email: editData.email || "",
          country_code: editData.country_code || "+1",
        });
      } else {
        setForm({
          first_name: "",
          middle_name: "",
          last_name: "",
          date_of_birth: null,
          gender: "",
          relationship_id: "",
          marital_status: null,
          phone: "",
          email: "",
          country_code: "+1",
        });
      }
      setErrors({});
      setPhotoRemoved(false);
    }
  }, [show, editData]);

  const handleChange = (field, value) => {
    let nextValue = value;

    if (field === "phone") {
      if (form.country_code === "+1") {
        nextValue = formatUSPhone(value);
      } else {
        nextValue = value.replace(/\D/g, "").slice(0, 15);
      }
    }

    let updatedForm = { ...form, [field]: nextValue };

    // Handle Relationship specific logic
    if (field === "relationship_id") {
      const selectedRel = relationships.find((r) => r._id === value);
      if (selectedRel) {
        if (selectedRel.key === "spouse") {
          updatedForm.marital_status = "married";
        } else {
          updatedForm.marital_status = null;
        }
      }
    }

    setForm(updatedForm);
    if (errors[field]) clearError(setErrors, field);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateData(memberSchema, form, setErrors);
    if (!isValid) return;

    const normalizedPayload = {
      ...form,
      date_of_birth: normalizeDateToUTC(form.date_of_birth),
      gender: form.gender.toLowerCase(),
      marital_status: form?.marital_status?.toLowerCase() || null,
      phone:
        form.country_code === "+1"
          ? normalizePhoneForSave(form.phone) // remove formatting for US number
          : form.phone.replace(/\D/g, ""),
    };

    if (onSaveLocal) {
      // Check for duplicate locally if adding new
      if (!editData) {
        const isEmailExist = form.email && existingItems.some(item => item.email && item.email.toLowerCase() === form.email?.toLowerCase());
        const isPhoneExist = normalizedPayload.phone && existingItems.some(item => item.phone === normalizedPayload.phone && item.country_code === form.country_code);

        if (isEmailExist) {
          setErrors(prev => ({ ...prev, email: { message: "email_already_added" } }));
          return;
        }
        if (isPhoneExist) {
          setErrors(prev => ({ ...prev, phone: { message: "phone_already_added" } }));
          return;
        }
      }

      onSaveLocal(normalizedPayload);
      onHide();
      return;
    }

    try {
      dispatch(SET_LOADING(true));
      const payload = normalizedPayload;

      if (editData?._id) {
        await API.put(
          `${api_paths.member.family_member.UPDATE_MEMBER}/${editData._id}`,
          payload
        );
        // If we have a pending image to upload
        if (selectedImageFile) {
          await uploadPhotoForMember(editData._id, selectedImageFile);
        }
        toastSuccessMessage("Member updated successfully");
      } else {
        const res = await API.post(api_paths.member.family_member.ADD_MEMBER, payload);
        const newId = res.data?.payload?.result?._id;
        // If we have a pending image to upload
        if (newId && selectedImageFile) {
          await uploadPhotoForMember(newId, selectedImageFile);
        }
        toastSuccessMessage("Member added successfully");
      }

      refreshList();
      onHide();
      setSelectedImageFile(null);
      setTempImagePreview(null);
    } catch (error) {
      console.log("Error saving member:", error);
      toastErrorMessage("Something went wrong while saving the member.");
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const handleImediatePhotoUpload = async (file) => {
    if (!file) return;
    if (!editData) {
      // Add mode: just set locally
      setSelectedImageFile(file);
      setPhotoRemoved(false);
      const reader = new FileReader();
      reader.onload = (e) => setTempImagePreview(e.target.result);
      reader.readAsDataURL(file);
      setShowPhotoModal(false);
    } else {
      // Edit mode: upload immediately
      try {
        setSavingPhoto(true);
        dispatch(SET_LOADING(true));
        await uploadPhotoForMember(editData._id, file);
        toastSuccessMessage("Profile photo updated");
        setPhotoRemoved(false);
        setTempImagePreview(URL.createObjectURL(file));
        if (refreshList) refreshList();
        setShowPhotoModal(false);
      } finally {
        setSavingPhoto(false);
        dispatch(SET_LOADING(false));
      }
    }
  };

  const handleImediatePhotoDelete = async () => {
    if (!editData) {
      // Add mode: clear locally
      setSelectedImageFile(null);
      setTempImagePreview(null);
      setPhotoRemoved(true);
      setShowPhotoDeleteModal(false);
    } else {
      // Edit mode: delete from server
      try {
        setSavingPhoto(true);
        dispatch(SET_LOADING(true));
        await API.delete(`${api_paths.member.family_member.DELETE_PROFILE}${editData._id}`);
        toastSuccessMessage("Profile photo removed");
        setTempImagePreview(null);
        setSelectedImageFile(null);
        setPhotoRemoved(true);
        if (refreshList) refreshList();
        setShowPhotoDeleteModal(false);
      } catch (error) {
        toastErrorMessage("Failed to delete profile photo.");
      } finally {
        setSavingPhoto(false);
        dispatch(SET_LOADING(false));
      }
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editData ? "Edit Family Member" : "Add Family Member"}
        </Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Body className="px-4 mx-1">
          <div className="row">
            <div className="col-md-4">
              <Field
                label="First Name"
                required
                error={errors.first_name?.message}
              >
                <input
                  type="text"
                  className={`form-control form-control-sm ${errors.first_name ? "is-invalid" : ""
                    }`}
                  value={form.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  placeholder="Enter first name"
                />
              </Field>
            </div>

            <div className="col-md-4">
              <Field
                label="Middle Name/Initial"
                error={errors.middle_name?.message}
              >
                <input
                  type="text"
                  className={`form-control form-control-sm ${errors.middle_name ? "is-invalid" : ""
                    }`}
                  value={form.middle_name}
                  onChange={(e) => handleChange("middle_name", e.target.value)}
                  placeholder="Enter middle name"
                />
              </Field>
            </div>

            <div className="col-md-4">
              <Field
                label="Last Name"
                required
                error={errors.last_name?.message}
              >
                <input
                  type="text"
                  className={`form-control form-control-sm ${errors.last_name ? "is-invalid" : ""
                    }`}
                  value={form.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  placeholder="Enter last name"
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Date of Birth" required>
                <DatePicker
                  futureNotAllowed={true}
                  value={form.date_of_birth}
                  onChange={(date) => handleChange("date_of_birth", date)}
                  error={errors.date_of_birth?.message}
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Gender" required>
                <SelectField
                  value={
                    genderOptions.find((o) => o.value === form.gender) || null
                  }
                  options={genderOptions}
                  onChange={(opt) => handleChange("gender", opt.value)}
                  error={errors.gender?.message}
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Relationship" required>
                <SelectField
                  value={
                    relationships
                      .map((r) => ({ value: r._id, label: r.name }))
                      .find((o) => o.value === form.relationship_id) || null
                  }
                  options={relationships.map((r) => ({
                    value: r._id,
                    label: r.name,
                  }))}
                  onChange={(opt) => handleChange("relationship_id", opt.value)}
                  error={errors.relationship_id?.message}
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field
                label="Marital Status"
                error={errors.marital_status?.message}
              >
                <SelectField
                  value={
                    maritalStatusOptions.find(
                      (o) => o.value === form.marital_status
                    ) || null
                  }
                  options={maritalStatusOptions}
                  onChange={(opt) => handleChange("marital_status", opt.value)}
                />
              </Field>
            </div>

            <div className="col-12 col-md-6">
              <Field label="Mobile Number">
                <div className="d-flex gap-2">
                  {/* Country Code Select */}
                  <div style={{ width: "120px" }}>
                    <SelectField
                      value={countryCodeOptions.find(
                        (opt) => opt.value === form.country_code
                      )}
                      options={countryCodeOptions}
                      onChange={(opt) => {
                        const newCode = opt.value;

                        setForm((prev) => ({
                          ...prev,
                          country_code: newCode,
                          phone:
                            newCode === "+1"
                              ? formatUSPhone(prev.phone)
                              : prev.phone.replace(/\D/g, ""),
                        }));
                      }}
                    />
                  </div>

                  {/* Phone Number Input */}
                  <input
                    type="tel"
                    placeholder="Enter Mobile Number"
                    className={`form-control form-control-sm ${errors.phone ? "is-invalid" : ""
                      }`}
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    maxLength={form.country_code === "+1" ? 15 : 10}
                  />
                </div>

                {errors.phone && (
                  <div className="invalid-feedback d-block">
                    {errors.phone.message}
                  </div>
                )}
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Email" error={errors.email?.message}>
                <input
                  type="email"
                  className={`form-control form-control-sm ${errors.email ? "is-invalid" : ""
                    }`}
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="Enter email"
                />
              </Field>
            </div>

            <div className="col-md-12 mb-3">
              <label className="form-label small mb-2 d-block">Profile Photo</label>
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 100,
                    height: 100,
                    position: "relative",
                  }}
                >
                  {profileImage ? (
                    <Image
                      src={profileImage}
                      roundedCircle
                      width={100}
                      height={100}
                      style={{
                        objectFit: "cover",
                        border: "1px solid #dee2e6",
                      }}
                      alt="Profile"
                    />
                  ) : (
                    <div
                      style={{
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        background: "#f2f2f2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        color: "#555",
                      }}
                    >
                      {initials}
                    </div>
                  )}

                  {/* EDIT PHOTO */}
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Edit photo</Tooltip>}
                  >
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        position: "absolute",
                        right: 0,
                        bottom: 0,
                        background: "#FEF7EA",
                        border: "none",
                        borderRadius: "50%",
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        zIndex: 1,
                      }}
                    >
                      <img src={editIcon} alt="edit" width={16} height={16} />
                    </button>
                  </OverlayTrigger>

                  {/* DELETE PHOTO */}
                  {(profileImage || tempImagePreview) && (
                    <button
                      type="button"
                      onClick={() => setShowPhotoDeleteModal(true)}
                      style={{
                        position: "absolute",
                        right: "3px",
                        top: "5px",
                        background: "var(--white)",
                        border: "1px solid #ED0505",
                        borderRadius: "50%",
                        width: 20,
                        height: 20,
                        color: "#ED0505",
                        fontSize: 15,
                        cursor: "pointer",
                        zIndex: 1,
                      }}
                    >
                      <Image
                        src={deleteIcon}
                        alt="close"
                        width={12}
                        height={12}
                        className="d-flex justify-content-center align-items-center w-100 h-100"
                        style={{ padding: "1px" }}
                      />
                    </button>
                  )}
                </div>
                <div className="text-muted small">
                  Allowed formats: JPG, PNG. Max size: 2MB.
                </div>
              </div>
            </div>
          </div>

          <Modal.Footer className="d-flex justify-content-start  border-top-0 p-0 my-3">
            <Button
              type="submit"
              className="btn btn-orange-lg m-0"
              disabled={loading}
            >
              {loading ? "Saving..." : editData ? "Save Changes" : "Add Member"}
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </form>

      <ProfilePhotoCropper
        show={showPhotoModal}
        onHide={() => setShowPhotoModal(false)}
        onSave={handleImediatePhotoUpload}
        initialImage={tempImagePreview || profileImage}
        saving={savingPhoto}
      />

      <ConfirmDeleteModal
        show={showPhotoDeleteModal}
        title="Remove profile photo?"
        message="Are you sure you want to remove the profile photo?"
        onConfirm={handleImediatePhotoDelete}
        onCancel={() => setShowPhotoDeleteModal(false)}
        confirmText="Delete"
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;

          const reader = new FileReader();
          reader.onload = (ev) => {
            setTempImagePreview(ev.target.result);
            setShowPhotoModal(true);
          };
          reader.readAsDataURL(file);

          e.target.value = "";
        }}
      />
    </Modal>
  );
};

export default MemberModal;
